import React, { useState, useEffect, useRef, useCallback } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import axios from "axios";
import debounce from 'lodash/debounce';
import { countries } from "countries-list";

const StepBookingDetails = ({
  guestDetails = {},
  setGuestDetails,
  selectedRooms = [],
  setSelectedRooms,
  checkInDate,
  checkOutDate,
  paymentSummary = {},
  handleBack,
  searchState,
  setSearchState,
  handleNext,
}) => {
  // 🟢 State for Guest Information (Persistent Across Steps)
  const [guestInfo, setGuestInfo] = useState(() => ({
    title: "",
    name: "",
    lastName: "",
    email: "",
    phone: "",
    gender: null,
    city: "",
    state: "",
    country: "",
    zip: "",
    nationality: "",
    company: "",
    fax: "",
    registrationNo: "",
    expected_time: "", // Added expected_time to guestInfo
    travelling_with_pet: false,
  }));

  // 🟢 State for Guest ID
  const [guestId, setGuestId] = useState(null); // Store guestId

  const currency = localStorage.getItem("currencycode");

  useEffect(() => {
    if (guestDetails && Object.keys(guestDetails).length > 0) {
      setGuestInfo(guestDetails);
      setGuestId(guestDetails.guestId || null); // Restore guestId if present
    }
  }, [guestDetails]);

  // 🟢 Track previous roomTypeIds to prevent unnecessary API calls
  const prevRoomTypeIdsRef = useRef(null);

  // 🟢 Attach taxDetails to selectedRooms only once on mount or when paymentSummary changes
  useEffect(() => {
    if (selectedRooms.length > 0 && paymentSummary && paymentSummary.taxDetails) {
      const hasTaxDetails = selectedRooms.every(room => room.taxDetails && room.taxDetails.length > 0);
      if (hasTaxDetails) return;

      const totalRoomCharges = selectedRooms.reduce((total, room) => {
        const nights = Math.max(
          1,
          Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))
        );
        const roomRate =
          (room.appliedRate?.single !== undefined
            ? room.appliedRate?.single
            : parseFloat(room.rate_single)) || 0;
        return total + (room.quantity || 1) * roomRate * nights;
      }, 0);

      const updatedRooms = selectedRooms.map((room) => {
        const nights = Math.max(
          1,
          Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))
        );
        const roomRate =
          (room.appliedRate?.single !== undefined
            ? room.appliedRate?.single
            : parseFloat(room.rate_single)) || 0;
        const roomCharges = (room.quantity || 1) * roomRate * nights;

        const proportion = totalRoomCharges > 0 ? roomCharges / totalRoomCharges : 0;

        const roomTaxDetails = paymentSummary.taxDetails.map((tax) => ({
          taxName: tax.taxName,
          taxRate: tax.taxRate,
          taxAmount: Number((tax.taxAmount * proportion).toFixed(2)),
        }));

        return {
          ...room,
          taxDetails: roomTaxDetails,
        };
      });

      setSelectedRooms(updatedRooms);
      localStorage.setItem("selectedRooms", JSON.stringify(updatedRooms));
    }
  }, [paymentSummary, checkInDate, checkOutDate, setSelectedRooms]);

  const timeOptions = Array.from({ length: 24 }, (_, i) => {
    const startHour = i;
    const endHour = (i + 1) % 24;
  
    const formatHour = (h) => {
      const period = h < 12 ? "AM" : "PM";
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `${hour12} ${period}`;
    };
  
    return {
      value: `${formatHour(startHour)} - ${formatHour(endHour)}`,
      label: `${formatHour(startHour)} - ${formatHour(endHour)}`
    };
  });
  

  // 🟢 State for Guest Search
  const asyncSelectRef = useRef(null); // Reference to AsyncSelect for manual control

  // 🟢 Define Title Options
  const titleOptions = [
    { value: "Mr", label: "Mr." },
    { value: "Mrs", label: "Mrs." },
    { value: "Miss", label: "Miss" },
    { value: "Dr", label: "Dr." },
  ];

  // 🟢 Define Nationality Options
  const nationalityOptions = [
    { value: "USA", label: "United States" },
    { value: "India", label: "India" },
    { value: "UK", label: "United Kingdom" },
    { value: "Germany", label: "Germany" },
    { value: "Canada", label: "Canada" },
  ];

  const countryOptions = Object.entries(countries)
    .map(([code, country]) => ({
      value: country.name,
      label: country.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [errors, setErrors] = useState({});
  const [roomAllocations, setRoomAllocations] = useState({});
  const [availableRooms, setAvailableRooms] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  // 🟢 State for Guest Search
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchField, setSearchField] = useState("name"); // Default to name

  // 🟢 Search Field Options
  const searchFieldOptions = [
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "name", label: "Name" },
  ];

  const formatGuestStayDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getGuestHistoryMeta = (guest) => {
    const previousStays = Number(guest?.previous_stays || 0);
    return {
      previousStays,
      isRepeat: previousStays > 0,
      lastStayLabel: formatGuestStayDate(guest?.last_stay_date),
    };
  };

  // 🟢 Debounced Function to Search Guests
  const loadGuestOptions = useCallback(
    debounce(async (inputValue) => {
      const minLength = searchField === "email" ? 2 : 2;
      if (!inputValue || inputValue.length < minLength) {
        console.log(`Input too short (min ${minLength}), skipping API call`);
        return [];
      }

      try {
        const params = { [searchField]: inputValue };
        console.log("Fetching guests with params:", params);

        const response = await axios.get(`${apiUrl}/booking/search-guest`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
          },
        });

        console.log("API response:", response.data);

        if (response.status === 204 || !response.data.success) {
          return [];
        }

        return response.data.guests.map((guest) => ({
          value: guest.guest_id,
          label: guest.full_name || guest.email || guest.phone_no || "Existing guest",
          guestData: guest,
        }));
      } catch (error) {
        console.error("Search error:", error);
        toast.error("Failed to search guests.");
        return [];
      }
    }, 300),
    [apiUrl, token, searchField]
  );

  const formatGuestOption = (option, { context }) => {
    const guest = option?.guestData || {};
    const { previousStays, isRepeat, lastStayLabel } = getGuestHistoryMeta(guest);
    const primaryContact = guest.email || guest.phone_no || "No contact saved";
    const secondaryContact =
      guest.email && guest.phone_no ? guest.phone_no : "";

    if (context === "value") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            {guest.full_name || option.label}
          </span>
          <span
            style={{
              flexShrink: 0,
              padding: "2px 7px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".03em",
              background: isRepeat ? "#ecfdf3" : "#eff6ff",
              color: isRepeat ? "#027a48" : "#175cd3",
              border: `1px solid ${isRepeat ? "#abefc6" : "#b2ddff"}`,
            }}
          >
            {isRepeat ? "REPEAT GUEST" : "EXISTING GUEST"}
          </span>
        </div>
      );
    }

    return (
      <div style={{ padding: "3px 1px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <strong style={{ color: "#101828", fontSize: 14 }}>
            {guest.full_name || option.label}
          </strong>
          <span
            style={{
              flexShrink: 0,
              padding: "3px 8px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".04em",
              background: isRepeat ? "#ecfdf3" : "#eff6ff",
              color: isRepeat ? "#027a48" : "#175cd3",
              border: `1px solid ${isRepeat ? "#abefc6" : "#b2ddff"}`,
            }}
          >
            {isRepeat ? `REPEAT · ${previousStays} STAY${previousStays === 1 ? "" : "S"}` : "EXISTING GUEST"}
          </span>
        </div>
        <div
          style={{
            marginTop: 4,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 12px",
            color: "#667085",
            fontSize: 12,
          }}
        >
          <span>{primaryContact}</span>
          {secondaryContact && <span>{secondaryContact}</span>}
          {isRepeat && lastStayLabel && <span>Last stay · {lastStayLabel}</span>}
        </div>
      </div>
    );
  };

  // 🟢 Handle Input Change for Search Query
  const handleSearchInputChange = (value, { action }) => {
    console.log("Search input changed:", value, "Action:", action);
    if (action === "input-change") {
      setSearchQuery(value);
      if (!value) {
        setSelectedGuest(null);
        setGuestId(null); // Reset guestId when search query is cleared
        setGuestInfo({
          title: "",
          name: "",
          lastName: "",
          email: "",
          phone: "",
          gender: null,
          city: "",
          state: "",
          country: "",
          zip: "",
          nationality: "",
          company: "",
          fax: "",
          registrationNo: "",
          expected_time: "",
          travelling_with_pet: false,
        });
        if (asyncSelectRef.current) {
          asyncSelectRef.current.clearValue();
          asyncSelectRef.current.setState({ inputValue: "", isLoading: false });
        }
      }
    }
    return value; // Crucial for AsyncSelect
  };

  // 🟢 Handle Guest Selection
  const handleGuestSelect = (selectedOption) => {
    console.log("Guest selected:", selectedOption);
    setSelectedGuest(selectedOption);
    if (selectedOption) {
      const { guestData } = selectedOption;
      setGuestId(guestData.guest_id); // Store guestId
      setGuestInfo({
        title: guestData.title || "",
        name: guestData.full_name || "",
        lastName: guestData.last_name || "",
        email: guestData.email || "",
        phone: guestData.phone_no || "",
        gender: guestData.gender?.trim() || null,
        city: guestData.city || "",
        state: guestData.state || "",
        country: guestData.country || "",
        zip: guestData.zip || "",
        nationality: guestData.nationality || "",
        company: guestData.company || "",
        fax: guestData.fax || "",
        registrationNo: guestData.registration_no || "",
        expected_time: guestData.expected_time || "",
        // Pet is stay-specific, so do not inherit it from an existing guest profile.
        travelling_with_pet: false,
      });
      setErrors({});
      setSearchQuery(""); // Clear search query
    } else {
      setGuestId(null); // Reset guestId when selection is cleared
      setGuestInfo({
        title: "",
        name: "",
        lastName: "",
        email: "",
        phone: "",
        gender: null,
        city: "",
        state: "",
        country: "",
        zip: "",
        nationality: "",
        company: "",
        fax: "",
        registrationNo: "",
        expected_time: "",
      });
      setSearchQuery("");
      setSelectedGuest(null);
    }
  };

  // 🟢 Function to Fetch Room Numbers from API
  const fetchRoomNumbers = async () => {
    if (!checkInDate || !checkOutDate || selectedRooms.length === 0) return;

    const roomTypeIds = selectedRooms.map((room) => room.id);
    const roomTypeIdsString = JSON.stringify(roomTypeIds);

    if (prevRoomTypeIdsRef.current === roomTypeIdsString) {
      return;
    }

    prevRoomTypeIdsRef.current = roomTypeIdsString;

    const requestData = {
      check_in: checkInDate,
      check_out: checkOutDate,
      room_type_ids: roomTypeIds,
    };

    try {
      const response = await axios.post(
        `${apiUrl}/booking/roomno-availability`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        const roomData = response.data.availableRooms.reduce((acc, room) => {
          if (!acc[room.room_type_id]) acc[room.room_type_id] = [];
          acc[room.room_type_id].push({ value: room.room_number, label: room.room_number });
          return acc;
        }, {});
        setAvailableRooms(roomData);
      }
    } catch (error) {
      toast.error("Failed to fetch available room numbers.");
    }
  };

  useEffect(() => {
    fetchRoomNumbers();
  }, [checkInDate, checkOutDate]);

  useEffect(() => {
    if (Object.keys(availableRooms).length === 0) return;

    const updatedAllocations = { ...roomAllocations };
    let hasChanges = false;

    selectedRooms.forEach((room) => {
      const roomTypeId = room.id;
      if (availableRooms[roomTypeId]?.length) {
        for (let i = 0; i < room.quantity; i++) {
          if (!updatedAllocations[roomTypeId]?.[i]) {
            updatedAllocations[roomTypeId] = updatedAllocations[roomTypeId] || {};
            updatedAllocations[roomTypeId][i] = availableRooms[roomTypeId][i % availableRooms[roomTypeId].length].value;
            hasChanges = true;
          }
        }
      }
    });

    if (hasChanges) {
      setRoomAllocations(updatedAllocations);
    }
  }, [availableRooms, selectedRooms]);

  const handleRoomNumberChange = (roomTypeId, index, selectedOption) => {
    setRoomAllocations((prev) => ({
      ...prev,
      [roomTypeId]: { ...prev[roomTypeId], [index]: selectedOption.value },
    }));
  };

  const validateForm = (info) => {
    let newErrors = {};

    if (!info?.name?.trim()) {
      newErrors.name = "Guest Name is required";
    }

    if (info?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) {
      newErrors.email = "Valid Email is required";
    }

    if (!info?.phone?.trim() || !/^\+?\d{10,15}$/.test(info.phone)) {
      newErrors.phone = "Valid phone number (10-15 digits) is required";
    }

    if (!info?.title) {
      newErrors.title = "Title is required";
    }

    if (!info?.nationality) {
      newErrors.nationality = "Nationality is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const petTravelOptions = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ];

  const handleNextSteps = () => {
    const latestGuestInfo = { ...guestInfo, guestId }; // Include guestId in guestInfo
    setGuestDetails(latestGuestInfo);

    if (!validateForm(latestGuestInfo)) {
      toast.error("Please fill in all required guest details before proceeding.");
      return;
    }

    if (selectedRooms.length === 0) {
      alert("No selected rooms detected before proceeding!");
      return;
    }

    const updatedSearchState = {
      ...searchState,
      checkInDate: new Date(checkInDate).toISOString(),
      checkOutDate: new Date(checkOutDate).toISOString(),
    };
    setSearchState(updatedSearchState);
    localStorage.setItem("searchState", JSON.stringify(updatedSearchState));

    handleNext({
      guestInfo: latestGuestInfo,
      selectedRooms: selectedRooms.map((room) => ({
        ...room,
        roomNumbers: [...Array(room.quantity)].map((_, i) => roomAllocations[room.id]?.[i] || "Not Assigned"),
      })),
      finalcheckInDate: new Date(checkInDate).toISOString(),
      finalcheckOutDate: new Date(checkOutDate).toISOString(),
      paymentSummary,
    });
  };

  return (
    <div className="container-">
      <div className="row d-flex">
        {/* Left Section: Guest Information */}
        <div className="col-md-9">
          <div className="card">
            <div className="form-bg">
              {/* Search Existing Guest Section */}
              <div
                className="mb-4"
                style={{
                  background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 72%)",
                  padding: "18px",
                  borderRadius: 12,
                  border: "1px solid #dbe7f5",
                  boxShadow: "0 8px 24px rgba(15, 43, 91, 0.05)",
                }}
              >
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <h5 className="text-primary mb-1">Find Existing Guest</h5>
                    <div style={{ color: "#667085", fontSize: 12 }}>
                      Search before creating a new profile to keep guest history connected.
                    </div>
                  </div>
                  <span
                    style={{
                      padding: "4px 9px",
                      borderRadius: 999,
                      background: "#f2f4f7",
                      color: "#475467",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: ".04em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    OPTIONAL
                  </span>
                </div>
                <div className="row border-bottom-">
                  <div className="col-md-4 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Search By</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={searchFieldOptions}
                            value={searchFieldOptions.find(option => option.value === searchField)}
                            onChange={(selected) => setSearchField(selected.value)}
                            placeholder="Select field..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-8 mb-3">
                    <div className="row align-items-center">
                      <div className="col-2">
                        <label className="form-label">Search Guest</label>
                      </div>
                      <div className="col-10">
                        <div className="form-input">
                          <AsyncSelect
                            key={searchField} // Re-mounts component on field change
                            ref={asyncSelectRef} // Attach ref
                            cacheOptions={false} // Disable caching
                            loadOptions={loadGuestOptions} // Direct reference
                            defaultOptions={false} // Don’t preload options
                            onInputChange={handleSearchInputChange}
                            onChange={handleGuestSelect}
                            placeholder={`Search by ${searchField}...`}
                            value={selectedGuest} // Ensure the selected guest is displayed
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            filterOption={null} // Disable default filtering
                            formatOptionLabel={formatGuestOption}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue?.length >= 2
                                ? "No existing guest found — continue with a new guest below."
                                : `Type at least 2 characters to search by ${searchField}.`
                            }
                            loadingMessage={() => "Searching guest history..."}
                            styles={{
                              control: (provided, state) => ({
                                ...provided,
                                minHeight: 42,
                                borderColor: state.isFocused ? "#84adff" : "#d0d5dd",
                                boxShadow: state.isFocused ? "0 0 0 3px rgba(46, 116, 255, 0.10)" : "none",
                                borderRadius: 8,
                                "&:hover": { borderColor: "#84adff" },
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                                borderRadius: 10,
                                overflow: "hidden",
                                boxShadow: "0 16px 36px rgba(16, 24, 40, 0.14)",
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                padding: "10px 12px",
                                backgroundColor: state.isSelected
                                  ? "#eff6ff"
                                  : state.isFocused
                                  ? "#f8fafc"
                                  : "#ffffff",
                                color: "#101828",
                              }),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedGuest?.guestData && (() => {
                  const guest = selectedGuest.guestData;
                  const { previousStays, isRepeat, lastStayLabel } = getGuestHistoryMeta(guest);

                  return (
                    <div
                      role="status"
                      style={{
                        marginTop: 2,
                        padding: "12px 14px",
                        borderRadius: 10,
                        border: `1px solid ${isRepeat ? "#abefc6" : "#b2ddff"}`,
                        background: isRepeat ? "#f6fef9" : "#f5faff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <strong style={{ color: "#101828", fontSize: 14 }}>
                              {guest.full_name || "Existing guest selected"}
                            </strong>
                            <span
                              style={{
                                padding: "3px 8px",
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: ".04em",
                                background: isRepeat ? "#dcfae6" : "#dbeafe",
                                color: isRepeat ? "#05603a" : "#1849a9",
                              }}
                            >
                              {isRepeat ? "REPEAT GUEST" : "EXISTING GUEST"}
                            </span>
                          </div>
                          <div style={{ marginTop: 4, color: "#667085", fontSize: 12 }}>
                            {isRepeat
                              ? `${previousStays} previous completed stay${previousStays === 1 ? "" : "s"}${lastStayLabel ? ` · Last stay ${lastStayLabel}` : ""}`
                              : "Saved guest profile selected. Details below have been pre-filled."}
                          </div>
                        </div>
                        <div
                          style={{
                            color: "#344054",
                            fontSize: 12,
                            textAlign: "right",
                          }}
                        >
                          <div>{guest.email || "No email saved"}</div>
                          <div>{guest.phone_no || "No phone saved"}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Personal Information */}
              <div className="mb-4">
                <h5 className="text-primary mb-3">Personal Information</h5>
                <div className="row border-bottom">
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">
                          Title <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={titleOptions}
                            placeholder="Select title..."
                            value={titleOptions.find(option => option.value === guestInfo?.title) || null}
                            onChange={(selected) => setGuestInfo(prev => ({ ...prev, title: selected.value }))}
                          />
                          {errors.title && <small className="text-danger d-block">{errors.title}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="John Doe"
                            name="name"
                            value={guestInfo?.name || ""}
                            onChange={handleInputChange}
                          />
                          {errors.name && <small className="text-danger d-block">{errors.name}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Gender</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={genderOptions}
                            placeholder="Select gender..."
                            value={genderOptions.find(option => option.value === guestInfo?.gender) || null}
                            onChange={(selected) => setGuestInfo(prev => ({ ...prev, gender: selected.value }))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">
                          Nationality <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={countryOptions}
                            placeholder="Select nationality..."
                            value={countryOptions.find(option => option.value === guestInfo?.nationality) || null}
                            onChange={(selected) => setGuestInfo(prev => ({ ...prev, nationality: selected.value }))}
                          />
                          {errors.nationality && <small className="text-danger d-block">{errors.nationality}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Email Address</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="john@example.com"
                            name="email"
                            value={guestInfo?.email || ""}
                            onChange={handleInputChange}
                          />
                          {errors.email && <small className="text-danger d-block">{errors.email}</small>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">
                          Mobile Number <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="+1 234 567 890"
                            name="phone"
                            value={guestInfo?.phone || ""}
                            onChange={handleInputChange}
                          />
                          {errors.phone && <small className="text-danger d-block">{errors.phone}</small>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Expected Time</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={timeOptions}
                            value={timeOptions.find((option) => option.value === guestInfo?.expected_time) || null}
                            onChange={(selected) => handleInputChange({ target: { name: "expected_time", value: selected ? selected.value : null } })}
                            placeholder="Select time"
                            isClearable
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                height: "38px",
                                minHeight: "38px",
                                borderRadius: "4px",
                                borderColor: "#ced4da",
                              }),
                              menu: (provided) => ({ ...provided, zIndex: 9999 }),
                              singleValue: (provided) => ({ ...provided, lineHeight: "normal" }),
                              valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
                            }}
                          />
                          {errors.expected_time && <small className="text-danger d-block">{errors.expected_time}</small>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Travelling with Pet</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={petTravelOptions}
                            value={petTravelOptions.find(
                              (option) => option.value === Boolean(guestInfo?.travelling_with_pet)
                            )}
                            onChange={(selected) =>
                              setGuestInfo((prev) => ({
                                ...prev,
                                travelling_with_pet: selected?.value === true,
                              }))
                            }
                            isSearchable={false}
                          />
                          <small className="text-muted d-block mt-1">Stay-specific and editable later.</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-4">
                <h5 className="text-primary mb-3">Address Information</h5>
                <div className="row border-bottom">
                  <div className="col-md-12 mb-3">
                    <div className="row align-items-center">
                      <div className="col-2">
                        <label className="form-label">Street Address</label>
                      </div>
                      <div className="col-10">
                        <div className="form-input full-width">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="123 Main Street"
                            name="address"
                            value={guestInfo?.address || ""}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">Country</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <Select
                            options={countryOptions}
                            value={countryOptions.find((option) => option.value === guestInfo?.country) || null}
                            onChange={(selected) => handleInputChange({ target: { name: "country", value: selected ? selected.value : null } })}
                            placeholder="Select country"
                            isClearable
                            isSearchable
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                height: "38px",
                                minHeight: "38px",
                                borderRadius: "4px",
                                borderColor: "#ced4da",
                              }),
                              menu: (provided) => ({ ...provided, zIndex: 9999 }),
                              singleValue: (provided) => ({ ...provided, lineHeight: "normal" }),
                              valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">State/Province</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="California"
                            name="state"
                            value={guestInfo?.state || ""}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">City</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Los Angeles"
                            name="city"
                            value={guestInfo?.city || ""}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="row align-items-center">
                      <div className="col-4">
                        <label className="form-label">ZIP/Postal Code</label>
                      </div>
                      <div className="col-8">
                        <div className="form-input">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="90001"
                            name="zip"
                            value={guestInfo?.zip || ""}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Payment Summary */}
        <div className="col-md-3 form-bg">
          <div className="card">
            <div className="card-header bg-primary text-white padding_10">
              Payment Summary
            </div>
            <div className="p-3 border rounded bg-white">
              {paymentSummary && paymentSummary.roomCharges ? (
                <>
                  <div className="d-flex justify-content-between border-bottom pb-2">
                    <span className="fw-bold text-secondary">Room Charges</span>
                    <span className="fw-bold">
                      {currency} {paymentSummary.roomCharges.toFixed(2)}
                    </span>
                  </div>

                  {paymentSummary.taxDetails && paymentSummary.taxDetails.map((tax, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between border-bottom pb-2"
                    >
                      <span className="fw-bold text-secondary">
                        {tax.taxName} ({tax.taxRate}%)
                      </span>
                      <span className="fw-bold">
                        {currency} {tax.taxAmount.toFixed(2)}
                      </span>
                    </div>
                  ))}

                  <div className="d-flex justify-content-between pt-2">
                    <span className="fw-bold fs-5 text-dark">Total</span>
                    <span className="fw-bold fs-5 text-primary">
                      {currency} {paymentSummary.totalAmount.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-danger">No payment summary available. Please go back and select a room.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selected Room Types Section */}
      <div className="col-md-12">
        <div className="card">
          <div className="card-header bg-primary text-white">
            Selected Room Types
          </div>
          <div className="card-body">
            <table className="table table-bordered">
             
<thead>
  <tr>
    <th>Type</th>
    <th>Room No.</th>
    <th>Arrival/Departure</th>
    <th>Guests</th>
    <th>Meal Plan</th>
    <th>Room Charge</th>
    <th>Tax</th>
    <th>Total</th>
  </tr>
</thead>
<tbody>
  {selectedRooms.length > 0 ? (
    selectedRooms.flatMap((room) =>
      [...Array(room.quantity)].map((_, roomIndex) => {
        if (!roomAllocations[room.id]?.[roomIndex] && availableRooms[room.id]?.length) {
          setRoomAllocations((prev) => ({
            ...prev,
            [room.id]: {
              ...prev[room.id],
              [roomIndex]: prev[room.id]?.[roomIndex] || availableRooms[room.id][roomIndex % availableRooms[room.id].length].value,
            },
          }));
        }

        let roomRate = room.appliedRate?.selected ?? room.originalRate?.single ?? parseFloat(room.rate_single);
        if (room.isComplimentary) {
          roomRate = 0;
        }

        const nights = Math.max(
          1,
          Math.ceil((new Date(checkOutDate) - new Date(checkInDate)) / (1000 * 60 * 60 * 24))
        );
        const roomCharges = roomRate * nights; // Per-room charge

        // Adjust tax details by dividing taxAmount by quantity to get per-room tax
        const adjustedTaxDetails = room.taxDetails
          ? room.taxDetails.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: tax.taxAmount / room.quantity, // Divide by quantity to get per-room tax
            }))
          : [];

        const roomTaxTotal = adjustedTaxDetails.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
        const roomTotal = room.isComplimentary || room.excludeTax
          ? roomCharges
          : roomCharges + roomTaxTotal;

        return (
          <tr key={`${room.id}-${roomIndex}`}>
            <td>{room.type}</td>
            <td>
              <Select
                options={availableRooms[room.id] || []}
                value={availableRooms[room.id]?.find(r => r.value === roomAllocations[room.id]?.[roomIndex]) || null}
                onChange={(selectedOption) => handleRoomNumberChange(room.id, roomIndex, selectedOption)}
                placeholder="Select Room"
              />
            </td>
            <td>
              {new Date(checkInDate).toLocaleDateString()} - {new Date(checkOutDate).toLocaleDateString()}
            </td>
            <td>{room.adults} Adults, {room.children} Children</td>
            <td>{room.mealPlan}</td>
            <td>{currency}{roomCharges.toFixed(2)}</td> {/* New Room Charge column */}
            <td>{currency}{roomTaxTotal.toFixed(2)}</td> {/* New Tax column */}
            <td>{currency}{roomTotal.toFixed(2)}</td> {/* Existing Total column */}
          </tr>
        );
      })
    )
  ) : (
    <tr>
      <td colSpan="8" className="text-center">No rooms selected.</td> {/* Updated colSpan to 8 */}
    </tr>
  )}
</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="button-group mt-4 d-flex justify-content-end gap-3">
        <button
          className="btn btn-secondary custom-large-button px-3"
          onClick={() => {
            console.log("🔄 Saving paymentSummary before going back:", paymentSummary);
            localStorage.setItem("billingSummary", JSON.stringify(paymentSummary));
            handleBack();
          }}
        >
          Back
        </button>

        <button className="btn btn-success px-3 custom-large-button" onClick={handleNextSteps}>
          Next Step
        </button>
      </div>
    </div>
  );
};

export default StepBookingDetails;